import React, { useState, useEffect } from 'react';
import {
  Input,
  Form,
  Button,
  Row,
  Col,
  Checkbox,
  Typography,
  Space,
  Modal,
  Upload,
} from 'antd';
import {
  DeleteOutlined,
  CopyOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  CameraOutlined,
} from '@ant-design/icons';

import { webServiceProvider } from 'shared';

import styled from 'styled-components';

const { Title, Text, Link } = Typography;

const LargeCheckbox = styled(Checkbox)`
  .ant-checkbox-inner {
    width: 24px;
    height: 24px;
    border-radius: 6px;
  }
  .ant-checkbox-inner:after {
    left: 30%;
    top: 45%;
    width: 8px;
    height: 14px;
    border-width: 3px;
  }
  .ant-checkbox {
    font-size: 28px;
  }
`;

const MobileUploadScaling = styled.div`
  @media (max-width: 800px) {
    .ant-upload.ant-upload-select,
    .ant-upload-list-item-container,
    .ant-upload-list-item {
      width: 64px !important;
      height: 64px !important;
      min-width: 64px !important;
      min-height: 64px !important;
      display: flex !important;
      justify-content: center !important;
      align-items: center;
    }
    .ant-upload-list-item-image,
    .ant-upload-list-item-thumbnail {
      width: 50px !important;
      height: 50px !important;
      min-width: 50px !important;
      min-height: 50px !important;
      font-size: 8px !important;
    }
  }
`;

export const checkBoxPlaceholders = ['Pass', 'Caution', 'Fail', 'N/A'];

const FormSection = ({ section, groupIndex, sectionIndex, form }) => {
  const [fileList, setFileList] = useState([]);
  const [showUpload, setShowUpload] = useState(section.images?.length > 0);

  useEffect(() => {
    if (Array.isArray(section.images) && section.images.length) {
      const imagesList = section.images.map((image, idx) => ({
        uid: `preloaded-${idx}`,
        name: `image_${idx}.png`,
        status: 'done',
        url: image,
      }));
      setFileList(imagesList);
    } else {
      setFileList([]);
    }
  }, [section.images]);

  const handleChange = ({ fileList }) => {
    setFileList(fileList);
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      console.log('You can only upload image files!');
    }
    return isImage || Upload.LIST_IGNORE;
  };

  const checkBoxIcons = ['✔️', '⚠️', '❌', '🚫'];

  const getCheckboxLabel = (idx) => {
    const label =
      section.checkboxLabels &&
      section.checkboxLabels[idx] &&
      section.checkboxLabels[idx].trim() !== ''
        ? section.checkboxLabels[idx]
        : checkBoxPlaceholders[idx] || `Checkbox ${idx + 1}`;
    return label;
  };

  const handleCheckboxChange = (idx) => {
    const checkboxSelections =
      form.getFieldValue([
        'groups',
        groupIndex,
        'sections',
        sectionIndex,
        'checkboxes',
      ]) || section.checkboxes;
    const newValue = checkboxSelections.map((_, i) => i === idx);
    form.setFields([
      {
        name: ['groups', groupIndex, 'sections', sectionIndex, 'checkboxes'],
        value: newValue,
      },
    ]);
  };

  return (
    <div
      style={{
        marginBottom: 8,
        padding: 12,
        background: '#fff',
        borderRadius: 8,
        border: '1px solid #f0f0f0',
        position: 'relative',
      }}
    >
      <Row
        gutter={25}
        align="bottom"
        style={{ marginBottom: 8 }}
        className="checkbox-row"
      >
        {section.checkboxes.map((checked, idx) => (
          <Col
            span={5}
            key={idx}
            style={{
              textAlign: 'center',
              minWidth: 60,
              padding: 0,
              wordBreak: 'break-word',
            }}
          >
            <Text>
              {checkBoxIcons[idx]}
              <span className="checkbox-label-text">
                {getCheckboxLabel(idx)}
              </span>
            </Text>
            <div>
              <Form.Item
                name={[
                  'groups',
                  groupIndex,
                  'sections',
                  sectionIndex,
                  'checkboxes',
                  idx,
                ]}
                valuePropName="checked"
                initialValue={checked}
                style={{
                  marginBottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <LargeCheckbox
                  style={{
                    width: 24,
                    height: 24,
                    marginTop: 8,
                    marginBottom: 8,
                  }}
                  onChange={() => handleCheckboxChange(idx)}
                />
              </Form.Item>
            </div>
          </Col>
        ))}
        <Col
          span={4}
          style={{ textAlign: 'center', paddingBottom: 16, paddingLeft: -16 }}
        >
          <CameraOutlined
            style={{ width: '22px', height: '22px', fontSize: '22px' }}
            onClick={() => setShowUpload(!showUpload)}
          />
        </Col>
      </Row>
      <Row gutter={24} align="middle" style={{ marginBottom: 8 }}>
        <Col span={8}>
          <Row>
            {/* <Col span={24}>
              <Text>Description:</Text>
            </Col> */}
            <Col span={24}>
              <Text>
                <b>
                  {section.description ? section.description : 'No Description'}
                </b>
              </Text>
            </Col>
          </Row>
        </Col>
        <Col span={16}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              width: '100%',
            }}
          >
            {/* <Text style={{ whiteSpace: 'normal', flexShrink: 0, overflowWrap: 'break-word', maxWidth: '30%' }}>{section.freeTextLabel || 'Notes: '}</Text> */}
            <Form.Item
              name={[
                'groups',
                groupIndex,
                'sections',
                sectionIndex,
                'freeText',
              ]}
              initialValue={section.freeText}
              style={{ marginTop: 4, marginBottom: 0, flex: 1 }}
            >
              <Input
                value={section.freeText}
                placeholder={section.freeTextLabel || 'Notes:'}
                style={{ marginTop: 4, marginLeft: 4, maxWidth: '95%' }}
              />
            </Form.Item>
          </div>
        </Col>
      </Row>
      {showUpload && (
        <MobileUploadScaling>
          <Row gutter={24} style={{ marginTop: 16 }}>
            <Col span={24}>
              <Text strong>Uploaded Images</Text>
              <Form.Item
                name={[
                  'groups',
                  groupIndex,
                  'sections',
                  sectionIndex,
                  'images',
                ]}
                valuePropName="fileList"
                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                style={{ marginTop: 8 }}
                initialValue={
                  section.images?.map((image, idx) => ({
                    uid: `preloaded-${idx}`,
                    name: `image_${idx}.png`,
                    status: 'done',
                    url: image,
                  })) || []
                }
              >
                <Upload
                  listType="picture-card"
                  beforeUpload={beforeUpload}
                  onChange={handleChange}
                  multiple
                  customRequest={async ({ file, onSuccess }) => {
                    const formData = new FormData();
                    formData.append('singleInputFileName', file);

                    let response = await webServiceProvider.postFormData(
                      'v1/inspection-forms/upload-image',
                      formData
                    );

                    file.url = response.url;
                    setTimeout(() => onSuccess('ok'), 0);
                  }}
                >
                  {fileList.length < 8 && (
                    <div>
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>Upload</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </MobileUploadScaling>
      )}
    </div>
  );
};

export default FormSection;
